import { AfterViewInit, Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { ConvertUnitDimensionRequest, UnitConversionApiService } from '@core/services/unit-conversion-api.service';
import { Dictionary } from '@ngrx/entity';
import { Unit, Units } from '@cco/model';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { NumberUtils } from '@app/shared/utils/number-utils';
import { Store } from '@ngrx/store';
import { selectResolvedSettingByPathAsNumber } from '@main/settings/store/settings.selectors';
import { ENTITY_SERVICE } from '@core/services/entity.service';
import { DefaultEntityService } from '@core/services/default-entity.service';
import { faEquals, faXmark } from '@fortawesome/free-solid-svg-icons';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { UnitSelectComponent } from '../../../units/containers/unit-select/unit-select.component';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatDialogContent } from '@angular/material/dialog';
import { HasNativeElement } from '@app/shared/interfaces/has-native-element';

type UnitIds = {lengthUnitId: number, widthUnitId: number, areaUnitId: number, heightUnitId: number, volumeUnitId: number}

@UntilDestroy()
@Component({
    selector: 'app-volume-calculator',
    templateUrl: './volume-calculator.component.html',
    styleUrls: ['./volume-calculator.component.scss'],
    imports: [MatDialogContent, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, UnitSelectComponent, FaIconComponent, MatDivider, MatButton]
})
export class VolumeCalculatorComponent implements OnInit, AfterViewInit {
  protected store = inject(Store);
  protected fb = inject(UntypedFormBuilder);
  protected entityService = inject<DefaultEntityService>(ENTITY_SERVICE);
  conversionApiService = inject(UnitConversionApiService);

  @Output() calculatedVolume = new EventEmitter();
  form: UntypedFormGroup;
  length$: Observable<string>;
  lengthUnit$: Observable<string>;
  width$: Observable<string>;
  widthUnit$: Observable<string>;
  area$: Observable<string>;
  areaUnit$: Observable<string>;
  calculatedArea$ = new BehaviorSubject<number>(null);
  height$: Observable<string>;
  heightUnit$: Observable<string>;
  calculatedVolume$ = new BehaviorSubject<number>(null);
  volumeUnit$: Observable<string>;

  validLengthUnitIds = [
    Units.METER.id,
    Units.MILLIMETER.id,
    Units.INCH.id,
    Units.FOOT.id,
    Units.YARD.id,
  ];
  validAreaUnitIds = [
    Units.SQUARE_FEET.id,
    Units.SQUARE_METERS.id,
    Units.SQUARE_YARDS.id,
  ];
  validVolumeUnitIds = [
    Units.CUBIC_METER.id,
    Units.CUBIC_YARD.id,
  ];
  units: Unit[];
  unitEntities: Dictionary<Unit>;
  defaultConcreteOutputUnitId: number;

  faMultiply = faXmark;
  faEquals = faEquals;

  constructor() {
    this.form = this.fb.group({
      length: null,
      lengthUnitId: null,
      width: null,
      widthUnitId: null,
      area: [null, Validators.required],
      areaUnitId: null,
      height: [null, Validators.required],
      heightUnitId: null,
      volume: [{value: null, disabled: true}],
      volumeUnitId: null,
    });

    this.length$ = this.form.get('length').valueChanges;
    this.lengthUnit$ = this.form.get('lengthUnitId').valueChanges;
    this.width$ = this.form.get('width').valueChanges;
    this.widthUnit$ = this.form.get('widthUnitId').valueChanges;
    this.area$ = this.form.get('area').valueChanges;
    this.areaUnit$ = this.form.get('areaUnitId').valueChanges;
    this.height$ = this.form.get('height').valueChanges;
    this.heightUnit$ = this.form.get('heightUnitId').valueChanges;
    this.volumeUnit$ = this.form.get('volumeUnitId').valueChanges;
  }

  ngOnInit() {
    // load data
    combineLatest([
      this.entityService.selectEntitiesAndLoadIfOlderThan<Unit>('Unit'),
      this.entityService.entityMap$<Unit>('Unit'),
      this.store.pipe(
        untilDestroyed(this),
        selectResolvedSettingByPathAsNumber(['defaultUnits', 'concreteOutput'])),
    ]).pipe(untilDestroyed(this)).subscribe(([units, unitEntities, defaultConcreteOutputUnitId]) => {
      const previousUnits: UnitIds = this.getUnitIdsFromLocalStorage();
      let unitIds: UnitIds;
      if (previousUnits) {
        // set units from previously used values in from localStorage
        unitIds = previousUnits;
      } else {
        // set units from default
        this.units = units;
        this.unitEntities = unitEntities;
        this.defaultConcreteOutputUnitId = defaultConcreteOutputUnitId ?? Units.CUBIC_METER.id;
        const desiredUnits = this.getDesiredUnitIdsMap(defaultConcreteOutputUnitId);
        unitIds = {
          lengthUnitId: desiredUnits.baseId,
          widthUnitId:  desiredUnits.baseId,
          areaUnitId:   desiredUnits.squaredId,
          heightUnitId: desiredUnits.baseId,
          volumeUnitId: desiredUnits.cubicId,
        };
      }
      this.form.patchValue(unitIds);
    });

    combineLatest([this.length$, this.width$, this.lengthUnit$, this.widthUnit$, this.areaUnit$])
      .pipe(untilDestroyed(this))
      .subscribe(([length, width, lengthUnitId, widthUnitId, areaUnitId]) => {
        const l = NumberUtils.castStringToNumber(length);
        const w = NumberUtils.castStringToNumber(width);

        this.calculateArea(l, w, +lengthUnitId, +widthUnitId, +areaUnitId);
    });

    this.calculatedArea$.subscribe(area => this.form.patchValue({area}));

    combineLatest([this.area$, this.height$, this.areaUnit$, this.heightUnit$, this.volumeUnit$]).pipe(untilDestroyed(this))
      .subscribe(([area, height, areaUnitId, heightUnitId, volumeUnitId]) => {
        const a = NumberUtils.castStringToNumber(area);
        const h = NumberUtils.castStringToNumber(height);

        this.calculateVolume(a, h, +areaUnitId, +heightUnitId, +volumeUnitId);
      });

    this.calculatedVolume$.pipe(untilDestroyed(this)).subscribe(volume => this.form.patchValue({volume}));

    combineLatest([
      this.calculatedVolume$,
      this.volumeUnit$,
    ]).pipe(untilDestroyed(this)).subscribe(([volume, unitId]) => this.calculatedVolume.emit({amount: volume, unitId}));

    combineLatest([
      this.lengthUnit$,
      this.widthUnit$,
      this.areaUnit$,
      this.heightUnit$,
      this.volumeUnit$,
    ]).pipe(
      untilDestroyed(this),
      distinctUntilChanged((a, b) =>
        a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3] && a[4] === b[4]),
    ).subscribe((unitIds) => {
      this.setUnitIdsInLocalStorage({
        lengthUnitId: +unitIds[0],
        widthUnitId: +unitIds[1],
        areaUnitId: +unitIds[2],
        heightUnitId: +unitIds[3],
        volumeUnitId: +unitIds[4],
      });
    });
  }

  setUnitIdsInLocalStorage(unitIds: UnitIds) {
    window.localStorage.setItem('volumeCalculatorUnitIds', JSON.stringify(unitIds));
  }

  getUnitIdsFromLocalStorage(): UnitIds {
    return JSON.parse(window.localStorage.getItem('volumeCalculatorUnitIds'));
  }

  calculateArea(length: number, width: number, lengthUnitId: number, widthUnitId: number, areaUnitId: number) {
    const allValuesExist = length != null && width != null && lengthUnitId != null && widthUnitId != null && areaUnitId != null;

    if (!allValuesExist) {
      this.calculatedArea$.next(null);
      return;
    }

    const meters = this.getDesiredUnitIdsMap(Units.CUBIC_METER.id);
    const yards = this.getDesiredUnitIdsMap(Units.CUBIC_YARD.id);

    if (areaUnitId === meters.squaredId) {
      // square meters
      if (lengthUnitId === widthUnitId && lengthUnitId === meters.baseId) {
        // direct calculation
        this.calculatedArea$.next(length * width);
      } else {
        const toConvert: ConvertUnitDimensionRequest = [
          {
            amount: length,
            fromUnitId: lengthUnitId,
            toUnitId: meters.baseId,
          },
          {
            amount: width,
            fromUnitId: widthUnitId,
            toUnitId: meters.baseId,
          },
        ];
        this.conversionApiService.convertBetweenDimensionsMultiple$(toConvert)
          .pipe(
            take(1),
            untilDestroyed(this)
          ).subscribe((values) => {this.calculatedArea$.next(values[0] * values[1]);});
      }
    } else if (areaUnitId === yards.squaredId) {
      // square yards
      if (lengthUnitId === widthUnitId && lengthUnitId === yards.baseId) {
        // direct calculation
        this.calculatedArea$.next(length * width);
      } else {
        const toConvert: ConvertUnitDimensionRequest = [
          {
            amount: length,
            fromUnitId: lengthUnitId,
            toUnitId: yards.baseId,
          },
          {
            amount: width,
            fromUnitId: widthUnitId,
            toUnitId: yards.baseId,
          },
        ];
        this.conversionApiService.convertBetweenDimensionsMultiple$(toConvert)
          .pipe(
            take(1),
            untilDestroyed(this)
          ).subscribe((values) => {this.calculatedArea$.next(values[0] * values[1]);});
      }
    } else {
      // square feet
      if (lengthUnitId === widthUnitId && lengthUnitId === yards.baseId) {
        // direct calculation
        this.calculatedArea$.next((length * 3) * (width * 3));
      } else {
        const toConvert: ConvertUnitDimensionRequest = [
          {
            amount: length,
            fromUnitId: lengthUnitId,
            toUnitId: yards.baseId,
          },
          {
            amount: width,
            fromUnitId: widthUnitId,
            toUnitId: yards.baseId,
          },
        ];
        this.conversionApiService.convertBetweenDimensionsMultiple$(toConvert)
          .pipe(
            take(1),
            untilDestroyed(this)
          ).subscribe((values) => {this.calculatedArea$.next((values[0] * 3) * (values[1] * 3));});
      }
    }
  }

  calculateVolume(area: number, height: number, areaUnitId: number, heightUnitId: number, volumeUnitId: number) {
    const allValuesExist = area != null && height != null && areaUnitId != null && heightUnitId != null && volumeUnitId != null;

    if (!allValuesExist) {
      this.calculatedVolume$.next(null);
      return;
    }

    const meters = this.getDesiredUnitIdsMap(Units.CUBIC_METER.id);
    const yards = this.getDesiredUnitIdsMap(Units.CUBIC_YARD.id);

    if (volumeUnitId === meters.cubicId) {
      // cubic meters
      if (areaUnitId === meters.squaredId && heightUnitId === meters.baseId) {
        // direct calculation
        this.calculatedVolume$.next(area * height);
      } else {
        const toConvert: ConvertUnitDimensionRequest = [
          {
            amount: area,
            fromUnitId: areaUnitId,
            toUnitId: meters.squaredId,
          },
          {
            amount: height,
            fromUnitId: heightUnitId,
            toUnitId: meters.baseId,
          },
        ];
        this.conversionApiService.convertBetweenDimensionsMultiple$(toConvert)
          .pipe(
            take(1),
            untilDestroyed(this)
          ).subscribe((values) => {this.calculatedVolume$.next(values[0] * values[1]);});
      }
    } else if (volumeUnitId === yards.cubicId) {
      // cubic yards
      if (areaUnitId === yards.squaredId && heightUnitId === yards.baseId) {
        // direct calculation
        this.calculatedVolume$.next(area * height);
      } else {
        const toConvert: ConvertUnitDimensionRequest = [
          {
            amount: area,
            fromUnitId: areaUnitId,
            toUnitId: yards.squaredId,
          },
          {
            amount: height,
            fromUnitId: heightUnitId,
            toUnitId: yards.baseId,
          },
        ];
        this.conversionApiService.convertBetweenDimensionsMultiple$(toConvert)
          .pipe(
            take(1),
            untilDestroyed(this)
          ).subscribe((values) => {this.calculatedVolume$.next(values[0] * values[1]);});
      }
    }
  }

  focus(): void {
    const nativeElement = (this.form.get('length') as unknown as HasNativeElement).nativeElement;

    if (nativeElement) {
      setTimeout(() => nativeElement.focus(), 50);
    }
  }

  ngAfterViewInit() {
    this.focus();
  }

  clear() {
    this.form.patchValue({
      length: null,
      width: null,
      area: null,
      height: null,
      volume: null,
    });
  }

  getDesiredUnitIdsMap(cubicUnitId: number): { baseId: number, squaredId: number, cubicId: number } {
    if (cubicUnitId === Units.CUBIC_METER.id) {
      return {
        baseId: Units.METER.id,
        squaredId: Units.SQUARE_METERS.id,
        cubicId: cubicUnitId,
      };
    }
    if (cubicUnitId === Units.CUBIC_YARD.id) {
      return {
        baseId: Units.YARD.id,
        squaredId: Units.SQUARE_YARDS.id,
        cubicId: cubicUnitId,
      };
    }
    return {baseId: null, squaredId: null, cubicId: cubicUnitId};
  }
}
